import { useState, useCallback } from "react";
import { APIClient } from "../services/api/ApiClient";
import { CompassError } from "../types/errorTypes";
import { AxiosRequestConfig } from "axios";
import { useApp } from "../store/AppProvider";
import { API_ROUTES } from "../services/api/config";
import {
  ValidationRequest,
  ValidationResponse,
  ValidationAPIResponse,
} from "../types/validationTypes";
import { FileLocation, FileOptionTabTitle } from "../types/fileUploadTypes";
import { FILE_VALIDATION_CONFIG } from "../pages/AddFile/constants";

interface UseValidationReturn {
  validationResponse: ValidationResponse | null;
  error: CompassError | null;
  isValidating: boolean;
  validate: (
    fileType: FileOptionTabTitle,
    location: FileLocation,
    params?: ValidationRequest["params"]
  ) => Promise<ValidationResponse | null>;
  resetValidation: () => void;
}

/**
 * Hook for running file validation
 * Uses config-based approach to determine validation endpoints
 */
export function useValidation(): UseValidationReturn {
  const { app } = useApp();
  const [validationResponse, setValidationResponse] =
    useState<ValidationResponse | null>(null);
  const [error, setError] = useState<CompassError | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const resetValidation = useCallback(() => {
    setValidationResponse(null);
    setError(null);
    setIsValidating(false);
  }, []);

  const validate = useCallback(
    async (
      fileType: FileOptionTabTitle,
      location: FileLocation,
      params?: ValidationRequest["params"]
    ): Promise<ValidationResponse | null> => {
      const config = FILE_VALIDATION_CONFIG[fileType];

      // If validation is not enabled for this file type, return success
      if (!config.enabled || !config.endpoint) {
        const successResponse: ValidationResponse = {
          validation_passed: true,
          result: {
            message: `${location.file}: Validation not required for this file type`,
          },
        };
        setValidationResponse(successResponse);
        return successResponse;
      }

      setIsValidating(true);
      setError(null);
      setValidationResponse(null);

      const requestConfig: AxiosRequestConfig = {
        headers: {
          token: app.authorisation.token,
          "Content-Type": "application/json",
        },
      };

      const payload: ValidationRequest = {
        location,
        params: params || [],
      };

      const apiClient = new APIClient();
      const endpoint = API_ROUTES[config.endpoint];

      try {
        const apiResponse = await apiClient.post<
          ValidationAPIResponse,
          ValidationRequest
        >(endpoint, payload, requestConfig);

        // Transform API response to normalized ValidationResponse
        const response: ValidationResponse = {
          validation_passed: apiResponse.data.validation_passed,
          result: apiResponse.data.result,
          data: apiResponse.data,
        };

        setValidationResponse(response);
        return response;
      } catch (err) {
        setError(err as CompassError);
        return null;
      } finally {
        setIsValidating(false);
      }
    },
    [app.authorisation.token]
  );

  return {
    validationResponse,
    error,
    isValidating,
    validate,
    resetValidation,
  };
}
